/**
 * Roles and permissions management page.
 * List with {@link CollapseReplaceWrapper} for create/edit forms.
 */

import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@/components/forms/Button';
import { CollapseReplaceWrapper } from '@/components/common/CollapseReplaceWrapper';
import { RolesDataTable } from '@/components/roles/RolesDataTable';
import { CreateRoleView } from '@/components/roles/CreateRoleView';
import { EditRoleView } from '@/components/roles/EditRoleView';
import { useDetailsView } from '@/hooks/useDetailsView';
import { useViewMode } from '@/hooks/useViewMode';
import { ManagementPage } from '@/components/common/ManagementPage';
import { usePageActions } from '@/hooks/usePageActions';

/** Which form is mounted in the collapse wrapper. */
type ActiveForm = 'create' | 'edit' | null;

/** Roles list with CollapseReplace create/edit. */
export const RolesManagementPage: React.FC = () => {
  const { canCreate, canEditRow, canDeleteRow, canExportCsv } = usePageActions('roles');
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const [activeForm, setActiveForm] = React.useState<ActiveForm>(null);
  const [editRoleId, setEditRoleId] = React.useState<string | null>(null);
  const { viewMode, setViewMode } = useViewMode('roles-management');
  const { openRowId, openRow, setOpenRow, closeRow } = useDetailsView();
  const contentRef = React.useRef<HTMLDivElement>(null);

  /** Deep-link: open create/edit/view via `?create=` / `?edit=` / `?view=`. */
  React.useEffect(() => {
    const viewId = searchParams.get('view');
    const editId = searchParams.get('edit');
    const shouldCreate = searchParams.get('create') === 'true';

    if (editId) {
      setEditRoleId(editId);
      setActiveForm('edit');
      closeRow();
    } else if (viewId) {
      setOpenRow(viewId);
      setActiveForm(null);
      setEditRoleId(null);
    } else if (shouldCreate) {
      setActiveForm('create');
      setEditRoleId(null);
      closeRow();
    }
  }, [searchParams, openRow, closeRow]);

  /** Close the form and drop the edit id so the next open starts clean. */
  const closeForm = React.useCallback(() => {
    setActiveForm(null);
    setEditRoleId(null);
  }, []);

  /** Swap the table for the edit form and close any open details row. */
  const handleEditRole = (roleId: string) => {
    setEditRoleId(roleId);
    setActiveForm('edit');
    closeRow();
  };

  /** Close the form and refresh the roles list. */
  const handleSuccess = React.useCallback(() => {
    setActiveForm(null);
    setEditRoleId(null);
    void queryClient.invalidateQueries({ queryKey: ['roles'] });
  }, [queryClient]);

  /** Pick the collapse-wrapper form from `activeForm`. */
  const formContent = React.useMemo(() => {
    if (activeForm === 'create') {
      return <CreateRoleView onClose={closeForm} onSuccess={handleSuccess} />;
    }
    if (activeForm === 'edit' && editRoleId) {
      return (
        <EditRoleView
          key={editRoleId}
          roleId={editRoleId}
          onClose={closeForm}
          onSuccess={handleSuccess}
        />
      );
    }
    return null;
  }, [activeForm, editRoleId, closeForm, handleSuccess]);

  /** Scroll the page to the form when it first opens. */
  const prevActiveFormRef = React.useRef(activeForm);
  React.useEffect(() => {
    if (prevActiveFormRef.current !== activeForm && activeForm !== null && contentRef.current) {
      contentRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    prevActiveFormRef.current = activeForm;
  }, [activeForm]);

  return (
    <ManagementPage
      title="Rôles & Permissions"
      subtitle="Créez, modifiez et configurez tous vos rôles"
      contentRef={contentRef}
      actions={
        canCreate ? (
          <Button
            preset="create-entity"
            onClick={() => {
              setActiveForm('create');
              setEditRoleId(null);
              closeRow();
            }}
          >
            Créer un rôle
          </Button>
        ) : undefined
      }
    >
      <CollapseReplaceWrapper
        mode="replace"
        isOpen={activeForm !== null}
        collapsePosition="top"
        formContent={formContent}
      >
        <RolesDataTable
          expandedRowId={openRowId}
          onToggleRow={openRow}
          onCloseDetails={closeRow}
          onEditRole={handleEditRole}
          enableFilters={true}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          pageActions={{ canEditRow, canDeleteRow, canExportCsv }}
        />
      </CollapseReplaceWrapper>
    </ManagementPage>
  );
};
